import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check, Loader2, MessageSquare, Phone, Store } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money } from "@/components/simple/product/Fields";
import { Fold, FoldBody } from "@/components/layout/Fold";
import CallPicker from "@/components/product/CallPicker";

const ACCENT = "#2563EB";

const Kicker = ({ children, testid }) => (
    <p className="font-mono text-[11px] font-bold uppercase tracking-[0.24em] text-zinc-500" data-testid={testid}>{children}</p>
);

/** Option 1 / Option 2 — the two ways a shop makes money on the same site. */
function Way({ w, dark }) {
    return (
        <div className={`flex flex-col border-2 p-6 ${dark ? "dark-block border-zinc-900 bg-zinc-950 text-white" : "border-zinc-900 bg-white"}`} data-testid={`way-${w.id}`}>
            <p className="font-mono text-[11px] font-bold uppercase tracking-[0.24em]" style={{ color: ACCENT }}>{w.kicker}</p>
            <h3 className={`mt-3 font-display text-2xl font-black leading-tight tracking-tight sm:text-3xl ${dark ? "text-white" : "text-zinc-900"}`}>{w.head}</h3>
            <p className={`mt-3 text-base leading-relaxed ${dark ? "text-zinc-300" : "text-zinc-600"}`}>{w.body}</p>
            <ul className="mt-4 space-y-2">
                {w.bullets.map((b) => (
                    <li key={b} className={`flex gap-2 text-sm ${dark ? "text-zinc-200" : "text-zinc-700"}`}><Check size={15} className="mt-0.5 shrink-0" style={{ color: ACCENT }} /> {b}</li>
                ))}
            </ul>
        </div>
    );
}

/** The one plan: what it costs the first month, then every month after. */
function PlanCard({ p, rate }) {
    return (
        <div className="border-2 border-zinc-900 bg-white p-6" data-testid={`whitelabel-plan-${p.id}`}>
            <p className="font-mono text-[11px] font-bold uppercase tracking-[0.24em] text-zinc-500">{p.name}</p>
            <p className="mt-2 font-display text-5xl font-black leading-none tracking-tight text-zinc-900" data-testid={`whitelabel-price-${p.id}`}>
                {money(p.first_month || p.price_month)}<span className="font-sans text-base font-normal text-zinc-500"> the first month</span>
            </p>
            {p.first_month && (
                <p className="mt-2 text-base text-zinc-700" data-testid={`whitelabel-then-${p.id}`}>
                    then <span className="font-bold text-zinc-900">{money(p.price_month)} a month</span> for as long as you stay. Cancel any time. 13thav keeps 0% of your sales.
                </p>
            )}
            <p className="mt-4 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500" data-testid="whitelabel-plan-tagline">What's in it</p>
            <ul className="mt-4 space-y-2" data-testid="whitelabel-perks">
                {p.perks.map((perk) => <li key={perk} className="flex gap-2 text-sm text-zinc-700"><Check size={15} className="mt-0.5 shrink-0" style={{ color: ACCENT }} /> {perk}</li>)}
            </ul>
            <Link to="/signup/shop" data-testid="whitelabel-start"
                className="mt-6 flex min-h-[52px] w-full items-center justify-center gap-2 bg-ink px-4 font-display text-base font-black uppercase tracking-wide text-[#fff] transition-transform hover:-translate-y-px">
                Start my shop <ArrowRight size={16} />
            </Link>
            <p className="mt-3 text-center text-xs text-zinc-500" data-testid="whitelabel-footnote">
                Seven screens, about five minutes. You pay after your shop is live. Consignment pays you {rate}% of every sale 13thav prints, plus your markup.
            </p>
        </div>
    );
}

function Bullets({ rows, testid }) {
    return (
        <dl className="mt-5 grid gap-4 sm:grid-cols-2" data-testid={testid}>
            {rows.map(([head, body]) => (
                <div key={head} className="border-l-2 border-zinc-900 pl-4">
                    <dt className="font-display text-base font-black tracking-tight text-zinc-900">{head}</dt>
                    <dd className="mt-1 text-sm leading-relaxed text-zinc-600">{body}</dd>
                </div>
            ))}
        </dl>
    );
}

/** `/whitelabel` (Phase 10): the pitch, form-first — the two options, the plan and Start my shop on top; everything else folded under it. */
export default function SimpleWhitelabel() {
    const [d, setD] = useState(null);
    const [callOpen, setCallOpen] = useState(() => typeof window !== "undefined" && window.location.hash === "#call");
    const { user } = useAuth();
    useEffect(() => {
        document.title = "Get this website for your print shop | 13thav";
        api.get("/signup/plan").then(({ data }) => setD(data)).catch(() => setD(false));
    }, []);

    if (d === null) return <div className="flex min-h-[60vh] items-center justify-center" data-testid="whitelabel-loading"><Loader2 className="animate-spin text-zinc-400" /></div>;
    const plan = (d?.plans || [])[0];
    const s = d?.sections || {};
    const biz = d?.business || {};
    const phoneRaw = biz.phone_raw || "";
    const openCall = () => { setCallOpen(true); setTimeout(() => document.getElementById("call")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50); };

    return (
        <div className="min-h-[100dvh] bg-white text-zinc-900" data-testid="whitelabel-page">
            <header className="sticky top-0 z-40 border-b-4 border-zinc-900 bg-white">
                <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
                    <Link to="/" className="flex items-center gap-3" data-testid="whitelabel-brand">
                        <span className="flex h-9 w-9 items-center justify-center bg-ink font-display text-base font-black text-[#fff]">13</span>
                        <span className="font-display text-xl font-black uppercase tracking-tight">13thav</span>
                        <span className="hidden font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500 sm:inline">· white-label print shop websites</span>
                    </Link>
                    <div className="flex items-center gap-3">
                        <Link to={d?.local?.url || "/"} className="hidden text-sm font-semibold text-zinc-700 underline md:block" data-testid="whitelabel-live-shop">See a real shop</Link>
                        <Link to={user ? "/account" : "/login?next=/signup/shop"} className="flex h-11 items-center border border-zinc-900 px-4 text-sm font-bold uppercase tracking-wide hover:bg-ink hover:text-[#fff]" data-testid="whitelabel-login">
                            {user ? "Back office" : "Log in"}
                        </Link>
                    </div>
                </div>
            </header>

            <main>
                <section className="mx-auto grid max-w-7xl gap-10 px-4 py-10 sm:px-6 lg:grid-cols-[1.1fr_.9fr] lg:py-16" data-testid="whitelabel-hero">
                    <div>
                        <Kicker testid="whitelabel-kicker">For print shops · rent the whole website</Kicker>
                        <h1 className="mt-4 font-display text-4xl font-black leading-[0.95] tracking-tight sm:text-5xl lg:text-6xl" data-testid="whitelabel-headline">{d?.headline}</h1>
                        <p className="mt-5 max-w-xl text-base leading-relaxed text-zinc-600 md:text-lg" data-testid="whitelabel-sub">{d?.sub}</p>
                        <ul className="mt-6 space-y-2" data-testid="whitelabel-lines">
                            {(d?.beats || d?.lines || []).map((l) => (
                                <li key={l} className="flex items-start gap-2 text-base font-medium text-zinc-800"><Check size={18} className="mt-0.5 shrink-0" style={{ color: ACCENT }} /> {l}</li>
                            ))}
                        </ul>
                        <div className="mt-8 flex flex-wrap items-center gap-3">
                            <Link to="/signup/shop" data-testid="whitelabel-start-top"
                                className="inline-flex min-h-[52px] items-center gap-2 bg-ink px-7 font-display text-base font-black uppercase tracking-wide text-[#fff] transition-transform hover:-translate-y-px">
                                Start my shop <ArrowRight size={16} />
                            </Link>
                            <button type="button" onClick={openCall} data-testid="whitelabel-book-call"
                                className="inline-flex min-h-[52px] items-center gap-2 border-2 border-zinc-900 px-6 font-display text-base font-black uppercase tracking-wide text-zinc-900 hover:bg-ink hover:text-[#fff]">
                                <Phone size={16} /> Book a 10-minute call
                            </button>
                            {phoneRaw && (
                                <a href={`sms:${phoneRaw}?body=I%20own%20a%20print%20shop%20and%20want%20this%20website.`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-zinc-700 underline" data-testid="whitelabel-text-us">
                                    <MessageSquare size={14} /> Text us {biz.phone}
                                </a>
                            )}
                        </div>
                        {d?.local?.live && (
                            <p className="mt-6 flex items-center gap-2 text-sm text-zinc-600" data-testid="whitelabel-proof-line">
                                <Store size={15} className="text-zinc-900" /> Proven on our own counter: <Link to={d.local.url} className="font-semibold text-zinc-900 underline">{d.local.name}, {d.local.city}</Link> takes every order on this exact site.
                            </p>
                        )}
                    </div>
                    {plan && <PlanCard p={plan} rate={d?.consignment_rate} />}
                </section>

                <section className="border-t-4 border-zinc-900 bg-zinc-50" data-testid="whitelabel-ways">
                    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
                        <Kicker>Two ways to make money on the same site</Kicker>
                        <h2 className="mt-3 font-display text-base font-black uppercase tracking-tight text-zinc-900 md:text-lg">You decide, product by product, who prints it.</h2>
                        <div className="mt-6 grid gap-4 lg:grid-cols-2">
                            {(d?.ways || []).map((w, i) => <Way key={w.id} w={w} dark={i === 0} />)}
                        </div>
                        <p className="mt-5 max-w-3xl text-base font-semibold text-zinc-900" data-testid="whitelabel-ways-note">{d?.ways_note}</p>
                        <div className="mt-8 grid grid-cols-2 gap-x-8 gap-y-6 border-y-2 border-zinc-900 py-6 sm:grid-cols-4" data-testid="whitelabel-money">
                            {(d?.money || []).map((m, i) => (
                                <div key={i}>
                                    <p className="font-display text-4xl font-black tabular-nums tracking-tight text-zinc-900">{m.value}</p>
                                    <p className="mt-1 text-sm leading-snug text-zinc-600">{m.label}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <Fold title="What you get — the seven menu items and the back office" kicker="Everything on this site, with your name on it" testid="wl-fold-gets">
                    <FoldBody>
                        <div className="grid gap-x-12 gap-y-9 sm:grid-cols-2">
                            {(s.gets || []).map((g) => (
                                <div key={g.key} data-testid={`get-${g.key}`}>
                                    <p className="font-mono text-[11px] font-bold tracking-[0.2em]" style={{ color: ACCENT }}>{g.num} · {g.meta}</p>
                                    <h3 className="mt-2 font-display text-lg font-black leading-tight tracking-tight text-zinc-900">{g.head}</h3>
                                    <p className="mt-2 text-sm leading-relaxed text-zinc-600">{g.body}</p>
                                    <p className="mt-2 flex flex-wrap gap-x-5 text-xs">
                                        <Link to={g.to} className="inline-flex items-center gap-1 font-semibold text-zinc-900 underline">See it as a customer <ArrowUpRight size={11} /></Link>
                                        <span className="text-zinc-500">You run it at <span className="font-semibold text-zinc-700">Back office → {g.office_label}</span></span>
                                    </p>
                                </div>
                            ))}
                        </div>
                    </FoldBody>
                </Fold>

                {(s.modes || []).map((m) => (
                    <Fold key={m.id} title={m.head} kicker={m.kicker} testid={`wl-fold-${m.id}`}>
                        <FoldBody>
                            <p className="max-w-3xl text-base leading-relaxed text-zinc-600">{m.body}</p>
                            <Bullets rows={m.bullets} testid={`wl-${m.id}-bullets`} />
                            <p className="mt-5 text-base font-semibold text-zinc-900">{m.foot}</p>
                            <Link to={m.office} className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-zinc-900 underline" data-testid={`wl-${m.id}-link`}>{m.office_label} <ArrowUpRight size={12} /></Link>
                        </FoldBody>
                    </Fold>
                ))}

                <Fold title="Price and terms — $199 the first month, then $99 a month, 0% of your sales" kicker="Nothing hidden" testid="wl-fold-terms">
                    <FoldBody><Bullets rows={s.terms || []} testid="wl-terms" /></FoldBody>
                </Fold>

                <Fold title={s.demo?.head || "Try it before you pay"} kicker={s.demo?.kicker} testid="wl-fold-demo">
                    <FoldBody>
                        <p className="max-w-3xl text-base leading-relaxed text-zinc-600">{s.demo?.local}</p>
                        {d?.local?.live && (
                            <Link to={d.local.url} className="mt-4 inline-flex min-h-[48px] items-center gap-2 bg-ink px-6 font-display text-sm font-black uppercase tracking-wide text-[#fff]" data-testid="wl-demo-shop">
                                <Store size={15} /> Shop {d.local.name} like a customer <ArrowUpRight size={14} />
                            </Link>
                        )}
                    </FoldBody>
                </Fold>

                <Fold title={s.support?.head || "Help"} kicker={s.support?.kicker} testid="wl-fold-support">
                    <FoldBody>
                        <p className="max-w-3xl text-base leading-relaxed text-zinc-600">{s.support?.body}</p>
                        <Bullets rows={s.support?.bullets || []} testid="wl-support-bullets" />
                        {phoneRaw && <a href={`tel:${phoneRaw}`} className="mt-5 inline-flex min-h-[48px] items-center gap-2 bg-ink px-6 font-display text-sm font-black uppercase tracking-wide text-[#fff]" data-testid="wl-support-phone"><Phone size={15} /> {biz.phone}</a>}
                    </FoldBody>
                </Fold>

                <Fold title={s.call?.head || "Book a 10-minute setup call"} kicker={s.call?.kicker} testid="wl-fold-call" id="call" open={callOpen} onToggle={setCallOpen}>
                    <FoldBody>
                        <div className="grid gap-8 lg:grid-cols-[.8fr_1.2fr]">
                            <div>
                                <p className="text-base leading-relaxed text-zinc-600" data-testid="wl-call-body">{s.call?.body}</p>
                                <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.14em] text-zinc-500">{s.call?.note}</p>
                            </div>
                            <div className="border-2 border-zinc-900 bg-white p-5"><CallPicker tier="Virtual Brick & Mortar" /></div>
                        </div>
                    </FoldBody>
                </Fold>

                <Fold title="Questions print shops ask" kicker="FAQ" testid="wl-fold-faq">
                    <FoldBody><Bullets rows={s.faq || []} testid="wl-faq" /></FoldBody>
                </Fold>

                <section className="dark-block border-t-4 border-zinc-900 bg-zinc-950 text-white" data-testid="whitelabel-close">
                    <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 sm:px-6 lg:grid-cols-[1fr_1fr]">
                        <div>
                            <h2 className="font-display text-3xl font-black leading-tight tracking-tight sm:text-4xl">{s.close?.head}</h2>
                            <p className="mt-4 max-w-xl text-base leading-relaxed text-zinc-400">{s.close?.body}</p>
                            <div className="mt-7 flex flex-wrap items-center gap-3">
                                <Link to="/signup/shop" className="inline-flex min-h-[52px] items-center gap-2 px-7 font-display text-base font-black uppercase tracking-wide text-white" style={{ background: ACCENT }} data-testid="whitelabel-start-bottom">
                                    Start my shop <ArrowRight size={16} />
                                </Link>
                                <button type="button" onClick={openCall} className="inline-flex min-h-[52px] items-center gap-2 border-2 border-white/30 px-6 font-display text-base font-black uppercase tracking-wide text-white hover:border-white" data-testid="whitelabel-call-bottom">
                                    <Phone size={16} /> Talk first
                                </button>
                            </div>
                            <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.14em] text-zinc-500">{s.close?.reassure}</p>
                        </div>
                        <ul className="grid gap-3 self-center">
                            {(s.close?.bullets || []).map((b) => <li key={b} className="flex gap-2.5 text-base font-medium text-zinc-100"><Check size={17} className="mt-0.5 shrink-0" style={{ color: ACCENT }} /> {b}</li>)}
                        </ul>
                    </div>
                    {s.quote && (
                        <div className="border-t border-white/10">
                            <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6">
                                <p className="font-display text-xl font-black leading-snug tracking-tight text-white sm:text-2xl">“{s.quote.body}”</p>
                                <p className="mt-4 text-sm font-semibold text-white">{s.quote.who}</p>
                                <p className="text-xs text-zinc-500">{s.quote.where}</p>
                            </div>
                        </div>
                    )}
                    <div className="border-t border-white/10 py-5">
                        <p className="text-center text-xs text-zinc-500">© {new Date().getFullYear()} 13thav · Brooklyn, NY · {d?.local?.live && <Link to={d.local.url} className="underline">Shop {d.local.name}</Link>}</p>
                    </div>
                </section>
            </main>
        </div>
    );
}
